import { Link } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
// mantine
import { ActionIcon, Menu, UnstyledButton } from '@mantine/core';
import { notifications } from '@mantine/notifications';
// icons
import {
    EllipsisVertical,
    Link as LinkIcon,
    MessageSquareText,
    Trash2
} from 'lucide-react';
// utils
import { buildURL } from '@newo/toolkit/url';
import { IConversationsUserPersona } from '@newo/api/gateway';
import { MENU_PORTAL } from '@/constants';
import { useDeletePersonaMutation } from '@/services/customer/personas/personas.queries.ts';
import { openConfirmDeleteModal } from '@/utils/ui.tsx';

interface ActionsProps {
    sessionId: string | null;
    contact: IConversationsUserPersona;
}

const handleClipboardCopy = async (value: string) => {
    await navigator.clipboard.writeText(value);
};

const Actions = ({ sessionId, contact }: ActionsProps) => {
    const queryClient = useQueryClient();
    const { mutate: deletePersona } = useDeletePersonaMutation();
    const link = buildURL('/history', {
        session_id: sessionId,
        user_persona_id: contact.id
    });
    const fullLink = buildURL(`${globalThis.location.origin}/history`, {
        session_id: sessionId,
        user_persona_id: contact.id
    });

    const handleDelete = () => {
        openConfirmDeleteModal({
            title: `Delete ${contact.name} contact?`,
            children: `Are you sure you want to delete ${contact.name} contact?`,
            confirmLabel: 'Delete Contact',
            onConfirm: () => {
                deletePersona(
                    { personaId: contact.id },
                    {
                        onSuccess: () => {
                            queryClient.invalidateQueries({
                                queryKey: ['contacts']
                            });
                            notifications.show({
                                title: 'Contact deleted',
                                message: 'Contact deleted successfully'
                            });
                        }
                    }
                );
            }
        });
    };

    return (
        <div className="flex items-center gap-4">
            <ActionIcon color="blue" component={Link} size="lg" to={link}>
                <MessageSquareText />
            </ActionIcon>
            <Menu
                withinPortal
                portalProps={{ target: `#${MENU_PORTAL}` }}
                shadow="md"
                width={250}
            >
                <Menu.Target>
                    <UnstyledButton>
                        <EllipsisVertical />
                    </UnstyledButton>
                </Menu.Target>
                <Menu.Dropdown>
                    <Menu.Item
                        color="red"
                        leftSection={<Trash2 />}
                        onClick={handleDelete}
                    >
                        Delete Contact
                    </Menu.Item>
                    <Menu.Item
                        leftSection={<LinkIcon />}
                        onClick={async () => handleClipboardCopy(fullLink)}
                    >
                        Copy transcription link
                    </Menu.Item>
                </Menu.Dropdown>
            </Menu>
        </div>
    );
};

export default Actions;
